/**
 * Activity Notes Tab Component for Event Details Dialog
 * Keep a timestamped log of all interactions and updates for events
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Clock, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ActivityNotesTab({ 
  booking, 
  notes = [], 
  onNotesUpdate
}) {
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);

  const formatNoteTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setAdding(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/notes`, {
        content: newNote.trim()
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      setNewNote('');
      onNotesUpdate();
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Clock className="w-5 h-5 text-yellow-500" />
          Activity Notes
        </h3>
      </div>
      
      <p className="text-sm text-muted-foreground">
        Keep a timestamped log of all interactions and updates for this event. Notes added on leads are preserved when converted to bookings.
      </p>
      
      {/* Add New Note */}
      <div className="bg-muted/30 rounded-lg p-4 space-y-3">
        <Label className="text-sm font-medium">Add New Note</Label>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Enter your note... (e.g., Called client, confirmed venue, discussed song list)"
          className="w-full h-24 px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-yellow-500/50 resize-none"
          data-testid="booking-note-input"
        />
        <div className="flex justify-between items-center">
          <p className="text-xs text-muted-foreground">
            Note will be timestamped automatically with current date/time and your name
          </p>
          <Button
            onClick={handleAddNote}
            disabled={!newNote.trim() || adding}
            className="bg-yellow-500 text-black hover:bg-yellow-400"
            data-testid="save-booking-note-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {adding ? 'Adding...' : 'Add Note'}
          </Button>
        </div>
      </div>
      
      {/* Existing Notes */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Activity History</Label>
        {notes.length === 0 ? (
          <div className="text-center py-8 bg-muted/20 rounded-lg">
            <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No notes yet</p>
            <p className="text-sm text-muted-foreground">Add your first note above to start tracking activity</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {[...notes].reverse().map((note, idx) => (
              <div key={note.id || idx} className="bg-background border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                  <Clock className="w-3 h-3" />
                  <span>{formatNoteTimestamp(note.timestamp)}</span>
                  <span className="text-yellow-500 font-medium">({note.author})</span>
                </div>
                <p className="text-foreground whitespace-pre-wrap">{note.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
